import { Box, Container, Grid, Link, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { API } from '../../../global/config/api';
import { useServerCall } from '../../../global/helpers/hooks';
import { BaseHeader, XsButton } from '../../../global/ui';
import { ICallRow, IRegionRestrictionRequest, IRegionRestrictionResponse } from '../../../swagger/external';

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function CallDetail() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const id = new URLSearchParams(location.search).get('id');

  const [callData, setCallData] = useState<ICallRow | null>(null);
  const [regionRestricted, setRegionRestricted] = useState<boolean>(false);

  // Načítanie zoznamu výziev a filtrovanie podľa id
  const callsCall = useServerCall<object, { rows: ICallRow[] }>({
    key: ['call_detail', id],
    mode: 'auto',
    request: { method: 'POST', url: API.calls(), data: {} },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (response) => {
      const rows = response.data?.rows ?? [];
      const found = rows.find((row) => row.ID === id) ?? null;
      setCallData(found);
    }
  });

  // Regionálne obmedzenie — mode: 'control', spúšťa sa ručne po načítaní callData
  const regionCall = useServerCall<IRegionRestrictionRequest, IRegionRestrictionResponse>({
    key: ['call_region_restriction', id],
    mode: 'control',
    request: { method: 'POST', url: API.regionRestriction(), data: { region_id: 'SK010' } },
    queryOptions: { refetchOnWindowFocus: false, cacheTime: 0 },
    onSuccess: (response) => {
      // is_allowed === false → BA región je pozastavený
      setRegionRestricted(response.data?.is_allowed === false);
    },
    onError: () => {
      setRegionRestricted(false);
    }
  });

  // Po načítaní výzvy spustíme kontrolu regionálneho obmedzenia (len pre SK010)
  useEffect(() => {
    if (callData) {
      regionCall.run();
    }
  }, [callData]);

  const handleCreateRequest = () => {
    // callId a callCode odovzdávame cez location state aj query param pre kompatibilitu
    history.push(`/request?callId=${callData?.ID ?? ''}&callCode=${callData?.code_ext ?? ''}`, {
      callId: callData?.ID,
      callCode: callData?.code_ext
    });
  };

  const handleBack = () => {
    history.push('/calls');
  };

  if (callsCall.isPending) {
    return (
      <>
        <BaseHeader />
        <Container maxWidth='lg'>
          <Box pt={4}>
            <Typography variant='body2'>{t('calls.loading')}</Typography>
          </Box>
        </Container>
      </>
    );
  }

  if (!callData) {
    return (
      <>
        <BaseHeader />
        <Container maxWidth='lg'>
          <Box pt={4}>
            <Typography variant='body2'>{t('calls.not_found')}</Typography>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <BaseHeader />
      <Container maxWidth='lg'>
        <Grid container direction='column'>
          <Grid item>
            <Box pt={2} pb={2}>
              <Typography variant='h4'>{callData.name_ext}</Typography>
            </Box>
          </Grid>
          <Grid item>
            <Typography variant='h5'>{callData.code_ext}</Typography>
          </Grid>
          <Grid item>
            <Box mt={1} mb={1} />
          </Grid>

          {/* Dátumy platnosti */}
          <Grid item>
            <Typography variant='body2'>
              <b>{t('calls.valid_from_label')}:</b> {formatDate(callData.valid_from)}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant='body2'>
              <b>{t('calls.valid_to_label')}:</b> {formatDate(callData.valid_to)}
            </Typography>
          </Grid>

          <Grid item>
            <Box mt={1} mb={1} />
          </Grid>

          {/* Upozornenie na regionálne obmedzenie (BA kraj) */}
          {regionRestricted && (
            <Grid item>
              <Typography variant='h6' style={{ color: '#c10b25' }}>
                {t('calls.region_restriction_warning')}
              </Typography>
              <Box mt={1} mb={1} />
            </Grid>
          )}

          {/* Základné informácie — HTML obsah */}
          {callData.basic_information && (
            <>
              <Grid item>
                <Typography variant='body2'>
                  <b>{t('calls.basic_information_label')}</b>
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant='body2'>
                  <span dangerouslySetInnerHTML={{ __html: callData.basic_information }} />
                </Typography>
              </Grid>
              <Grid item>
                <Box mt={1} mb={1} />
              </Grid>
            </>
          )}

          {/* Odkaz na dokumentáciu */}
          <Grid item>
            <Typography variant='body2'>
              <b>{t('calls.documentation_label')}</b>
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant='body2'>
              <Link
                style={{ cursor: 'pointer' }}
                onClick={() => window.open('https://www.siea.sk/inovacie/inovacnepoukazky/', '_blank')}
              >
                www.siea.sk/inovacie/inovacnepoukazky
              </Link>
            </Typography>
          </Grid>

          <Grid item>
            <Box mt={2} mb={1} />
          </Grid>

          {/* Akčné tlačidlá */}
          <Grid item xs={12} style={{ display: 'flex', gap: 8 }}>
            <XsButton variant='contained' label='calls.create_request_button' onClick={handleCreateRequest} />
            <XsButton variant='outlined' label='calls.cancel_button' onClick={handleBack} />
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default observer(CallDetail);
